import React from 'react';
import { Task, WaitingStatus, UrgencyLevel } from '../../types/task.types';
import { TaskItem } from '../TaskItem';
import { EmptyState } from '../EmptyState';
import { Loading } from '@/ui/components/atoms/Loading';
import './TaskList.styles.css';

interface VoicemailData {
  emailId: string;
  attachmentIndex: number;
}

interface TaskListProps {
  tasks: Task[];
  title: string;
  emptyMessage: string;
  onMarkDone: (taskId: string) => void;
  onUnmarkDone?: (taskId: string) => void;
  onMarkUnread?: (taskId: string) => void;
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onOpenChat: (task: Task) => void;
  onAssign?: (task: Task) => void;
  onViewEmail?: (emailId: string) => void;
  onStatusChange?: (taskId: string, newStatus: WaitingStatus) => void;
  onUrgencyChange?: (taskId: string, newUrgency: UrgencyLevel) => void;
  isLoading?: boolean;
  currentUserId: string;
  showCreateButton?: boolean;
  onCreateTask?: () => void;
  density?: 'comfortable' | 'compact';
  userRole?: string;
  voicemailMap?: Map<string, VoicemailData>;
  onAudioPlay?: (taskId: string) => void;
  activeAudioTaskId?: string | null;
}

export const TaskList: React.FC<TaskListProps> = ({
  tasks,
  title,
  emptyMessage,
  onMarkDone,
  onUnmarkDone,
  onMarkUnread,
  onEdit,
  onDelete,
  onOpenChat,
  onAssign,
  onViewEmail,
  onStatusChange,
  onUrgencyChange,
  isLoading = false,
  currentUserId,
  showCreateButton = false,
  onCreateTask,
  density = 'comfortable',
  userRole
}) => {
  // Sort tasks: pending first (by criticity and deadline), then completed
  const sortedTasks = [...tasks].sort((a, b) => {
    // Completed tasks go to the bottom
    if (a.is_done && !b.is_done) return 1;
    if (!a.is_done && b.is_done) return -1;
    
    // For pending tasks, sort by criticity
    const criticityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
    const criticityDiff = criticityOrder[a.criticity] - criticityOrder[b.criticity];
    if (criticityDiff !== 0) return criticityDiff;
    
    // Then by deadline (earlier deadlines first)
    if (a.deadline && b.deadline) {
      return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
    }
    if (a.deadline && !b.deadline) return -1;
    if (!a.deadline && b.deadline) return 1;
    
    // Finally by creation date (newer first)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const pendingTasks = sortedTasks.filter(t => !t.is_done);
  const completedTasks = sortedTasks.filter(t => t.is_done);
  const totalCount = tasks.length;
  const pendingCount = pendingTasks.length;
  const completedCount = completedTasks.length;

  return (
    <div className="mes-taches-list-container">
      <div className="mes-taches-list-header">
        <h3>{title}</h3>
        <div className="mes-taches-task-stats">
          {pendingCount > 0 && <span className="mes-taches-stat-badge pending">{pendingCount} en cours</span>}
          {completedCount > 0 && <span className="mes-taches-stat-badge completed">{completedCount} terminées</span>}
        </div>
      </div>

      <div className="mes-taches-list-content">
        {isLoading ? (
          <Loading size="medium" text="Chargement des tâches..." />
        ) : sortedTasks.length === 0 ? (
          <EmptyState
            type={emptyMessage.includes('recherche') ? 'no-results' : 'no-tasks'}
            message={emptyMessage}
            actionLabel={showCreateButton ? 'Créer une tâche' : undefined}
            onAction={showCreateButton ? onCreateTask : undefined}
          />
        ) : (
          <div className="mes-taches-tasks-wrapper">
            {sortedTasks.map(task => {
              // Check if user can edit or delete
              const canEdit = task.created_by === currentUserId || 
                             (task.assigned_user_ids?.includes(currentUserId) ?? false);
              const canDelete = task.created_by === currentUserId;

              return (
                <TaskItem
                  key={task.id}
                  task={task}
                  onMarkDone={onMarkDone}
                  onUnmarkDone={onUnmarkDone}
                  onMarkUnread={onMarkUnread}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onOpenChat={onOpenChat}
                  onAssign={onAssign}
                  onViewEmail={onViewEmail}
                  onStatusChange={onStatusChange}
                  onUrgencyChange={onUrgencyChange}
                  isLoading={isLoading}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  density={density}
                  userRole={userRole}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};